import React, { createContext, useContext, useReducer, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { createSite } from '../firebase/siteCore';
import { saveDraft, getDrafts, deleteDraft } from '../firebase/siteDrafts';
import aiService from '../services/aiService';
import { auth } from '../firebase/config';

// Configuration for sections that will store AI-generated text as { value, groundingMetadata }
export const sectionsConfig = [
  { field: 'overviewIntroduction', title: 'Overview / Introduction' },
  { field: 'discoveryAndExcavation', title: 'Discovery and Excavation' },
  { field: 'historicalContextAndChronology_narrative', title: 'Historical Context & Chronology' },
  { field: 'keyArchitecturalFeaturesLayout', title: 'Key Architectural Features / Layout' },
  { field: 'purposeAndFunction', title: 'Purpose and Function' },
  { field: 'culturalSignificance_narrative', title: 'Cultural Significance (Narrative)' },
  { field: 'notableFindsArtifacts_narrative', title: 'Notable Finds / Artifacts (Narrative)' },
  { field: 'theoriesAndInterpretations', title: 'Theories and Interpretations' },
  { field: 'currentStatusAndPreservation_narrative', title: 'Current Status and Preservation (Narrative)' },
];

// Initial state
const initialState = {
  // Form data
  siteData: {
    name: '',
    description: '',
    location: { 
      address: '', 
      coordinates: { lat: null, lng: null }, 
      region: '', 
      country: '' 
    },
    historicalPeriod: '', // New top-level field from aiService
    historicalSignificance: '', // Renamed from culturalSignificance
    keyDiscoveries: [], // New array of strings
    currentStatus: '', // New string field
    
    // Derived/processed era fields
    eraDisplay: '', // User-friendly display string for the period
    startEra: '', // Derived start era (e.g., "3000 BCE")
    endEra: '', // Derived end era (e.g., "1500 BCE")
    
    features: [], // Retained, potentially populated from AI, should be an array
    constructionPeriods: [], // Array of { periodName, startEra, endEra, features, details, isMain, startEraApproximate, endEraApproximate }
    media: [],
    
    // Sources and Citations
    sources: [], // Raw sources from backend (new field)
    // citations: [], // Processed citations for display - MOVED TO BE PART OF siteData
    groundingArticles: [], // Articles from grounding/citation metadata

    // Citations managed within the context
    citations: [], // Array of citation objects { number, type, title, author, ... }

    // Metadata
    groundingMetadata: null,
    citationMetadata: null,
    modelInfo: { // Updated structure
      retrievalModel: null,
      structuringModel: null,
    },
    usageMetadata: null, // Retained if provided
    spellCheck: null, // From aiService's checkSiteName
    // citations: [], // This was redundant, primary is at siteData.citations on line 55
  },

  // Wizard state
  currentStep: 0,
  isDirty: false,
  validationErrors: {},
  
  // Draft management
  drafts: [],
  currentDraftId: null,
  savingDraft: false,
  
  // AI integration
  aiLoading: false,
  groundingInfo: null,
  spellCheckData: null,
  searchResults: [],
  searchQuery: '',
  
  // UI state
  showExitModal: false,
  limitReachedModal: false,
  duplicateName: false,
};

// Action types
const ActionTypes = {
  SET_SITE_DATA: 'SET_SITE_DATA',
  SET_CURRENT_STEP: 'SET_CURRENT_STEP',
  SET_VALIDATION_ERRORS: 'SET_VALIDATION_ERRORS',
  SET_DRAFTS: 'SET_DRAFTS',
  SET_SAVING_DRAFT: 'SET_SAVING_DRAFT',
  SET_CURRENT_DRAFT_ID: 'SET_CURRENT_DRAFT_ID',
  SET_AI_LOADING: 'SET_AI_LOADING',
  SET_GROUNDING_INFO: 'SET_GROUNDING_INFO',
  SET_SPELL_CHECK_DATA: 'SET_SPELL_CHECK_DATA',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SHOW_EXIT_MODAL: 'SET_SHOW_EXIT_MODAL',
  SET_LIMIT_REACHED_MODAL: 'SET_LIMIT_REACHED_MODAL',
  SET_IS_DIRTY: 'SET_IS_DIRTY', // Added for explicit control over isDirty
  RESET_STATE: 'RESET_STATE',
  LOAD_DRAFT: 'LOAD_DRAFT',
  SET_DUPLICATE_NAME: 'SET_DUPLICATE_NAME'
};

// Reducer
function createSiteReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_SITE_DATA:
      return {
        ...state,
        siteData: {
          ...state.siteData,
          ...action.payload
        },
        isDirty: true
      };
    
    case ActionTypes.SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.payload
      };
    
    case ActionTypes.SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: action.payload
      };
    
    case ActionTypes.SET_DRAFTS:
      return {
        ...state,
        drafts: action.payload
      };
    
    case ActionTypes.SET_SAVING_DRAFT:
      return {
        ...state,
        savingDraft: action.payload
      };
    
    case ActionTypes.SET_CURRENT_DRAFT_ID:
      return {
        ...state,
        currentDraftId: action.payload
      };
    
    case ActionTypes.SET_AI_LOADING:
      return {
        ...state,
        aiLoading: action.payload
      };
    
    case ActionTypes.SET_GROUNDING_INFO:
      return {
        ...state,
        groundingInfo: action.payload
      };
    
    case ActionTypes.SET_SPELL_CHECK_DATA:
      return {
        ...state,
        spellCheckData: action.payload
      };
      
    case ActionTypes.SET_SEARCH_RESULTS:
      return {
        ...state,
        searchResults: action.payload
      };
      
    case ActionTypes.SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload
      };
      
    case ActionTypes.SET_SHOW_EXIT_MODAL:
      return {
        ...state,
        showExitModal: action.payload
      };
    
    case ActionTypes.SET_LIMIT_REACHED_MODAL:
      return {
        ...state,
        limitReachedModal: action.payload
      };

    case ActionTypes.SET_IS_DIRTY: // Added case for SET_IS_DIRTY
      return {
        ...state,
        isDirty: action.payload
      };
    
    case ActionTypes.LOAD_DRAFT:
      const loadedSiteData = action.payload.siteData || {};
      const citationsFromDraft = loadedSiteData.citations;
      
      const processedCitationsOnLoad = Array.isArray(citationsFromDraft)
        ? citationsFromDraft.map((cit, index) => {
            const { number, title, url, type, ...rest } = cit || {};
            return {
              number: number !== undefined ? number : index + 1,
              title: title || '',
              url: url || '',
              type: type || 'unknown',
              ...rest
            };
          })
        : [];

      // Start with a full structure from initialState.siteData
      const finalSiteData = { ...initialState.siteData };

      // Iterate over keys in loadedSiteData to merge them intelligently
      for (const key in loadedSiteData) {
        if (Object.prototype.hasOwnProperty.call(loadedSiteData, key)) {
          if (key === 'citations') { // Citations handled separately after this loop
            continue;
          }

          const initialValue = initialState.siteData[key];
          const loadedValue = loadedSiteData[key];

          // Check if both initial and loaded values for the key are non-null, non-array objects
          if (typeof initialValue === 'object' && initialValue !== null &&
              typeof loadedValue === 'object' && loadedValue !== null &&
              !Array.isArray(initialValue) && !Array.isArray(loadedValue)) {
            
            if (key === 'location') {
              finalSiteData[key] = {
                ...initialValue, // e.g., { address: '', coordinates: { lat: null, lng: null }, region: '', country: '' }
                ...loadedValue,  // e.g., { address: '123 Main' } or { address: '123 Main', coordinates: { lat: 10 } }
                coordinates: { // Deep merge coordinates specifically
                  ...(initialValue?.coordinates || { lat: null, lng: null }), // Ensure base structure for coordinates
                  ...(loadedValue?.coordinates || {}),
                }
              };
            } else {
              // For other nested objects (e.g., modelInfo, or section fields like { value, groundingMetadata })
              // Spread initialValue first to ensure all its keys are present, then spread loadedValue to overwrite.
              finalSiteData[key] = {
                ...initialValue,
                ...loadedValue
              };
            }
          } else {
            // For non-objects, arrays, or if one of the values is not an object, the loaded value takes precedence.
            // This also handles cases where a field might exist in loadedSiteData but not in initialState (though less likely with current setup).
            finalSiteData[key] = loadedValue;
          }
        }
      }
      
      // Ensure all sectionConfig fields are properly structured if they were part of the merge or initial state
      sectionsConfig.forEach(section => {
        const fieldKey = section.field;
        // Ensure the field exists as an object and has 'value' and 'groundingMetadata'
        if (!finalSiteData[fieldKey] || typeof finalSiteData[fieldKey] !== 'object' || finalSiteData[fieldKey] === null) {
          finalSiteData[fieldKey] = { value: '', groundingMetadata: { groundingSupports: [] } };
        } else {
          if (finalSiteData[fieldKey].value === undefined) {
            finalSiteData[fieldKey].value = '';
          }
          if (finalSiteData[fieldKey].groundingMetadata === undefined || typeof finalSiteData[fieldKey].groundingMetadata !== 'object' || finalSiteData[fieldKey].groundingMetadata === null) {
            finalSiteData[fieldKey].groundingMetadata = { groundingSupports: [] };
          } else if (finalSiteData[fieldKey].groundingMetadata.groundingSupports === undefined) {
            finalSiteData[fieldKey].groundingMetadata.groundingSupports = [];
          }
        }
      });
      
      // Assign processed citations
      finalSiteData.citations = processedCitationsOnLoad;

      return {
        ...state,
        siteData: finalSiteData,
        currentDraftId: action.payload.id,
        isDirty: false, // Should be false after loading a draft
        validationErrors: {}
      };

    case ActionTypes.RESET_STATE:
      return initialState;

    case ActionTypes.SET_DUPLICATE_NAME:
      return {
        ...state,
        duplicateName: action.payload,
      };
      
    default:
      return state;
  }
}

// Create context
export const CreateSiteContext = createContext();

// Helper function to re-scope citation segments
export function generateFieldSpecificGroundingMetadata(sectionsConfig, aiData, siteWideGroundingMetadata) {
  let currentOffset = 0;
  const allFieldsMetadata = {};

  if (!siteWideGroundingMetadata || !siteWideGroundingMetadata.groundingSupports) {
    // If no site-wide grounding, all fields get minimal metadata
    for (const section of sectionsConfig) {
      const fieldKey = section.field;
      allFieldsMetadata[fieldKey] = { groundingSupports: [] }; // Or a deep copy of an empty structure
    }
    return allFieldsMetadata;
  }

  for (const section of sectionsConfig) {
    const fieldKey = section.field;
    const fieldValue = typeof aiData[fieldKey] === 'string' ? aiData[fieldKey] : '';
    
    // Create a deep copy for this field's metadata
    const fieldGroundingMetadata = JSON.parse(JSON.stringify(siteWideGroundingMetadata));

    if (fieldGroundingMetadata.groundingSupports && Array.isArray(fieldGroundingMetadata.groundingSupports)) {
      for (const support of fieldGroundingMetadata.groundingSupports) {
        if (support.citationSegments && Array.isArray(support.citationSegments)) {
          const newCitationSegments = [];
          for (const segment of support.citationSegments) {
            const originalStartIndex = segment.startIndex;
            const originalEndIndex = segment.endIndex;

            // Calculate the field's span in the original concatenated text
            const fieldStartInConcatenated = currentOffset;
            const fieldEndInConcatenated = currentOffset + fieldValue.length;

            // Check if the segment overlaps with the current field's text
            const overlaps = originalStartIndex < fieldEndInConcatenated && originalEndIndex > fieldStartInConcatenated;

            if (overlaps) {
              const newStartIndex = Math.max(0, originalStartIndex - currentOffset);
              const newEndIndex = Math.min(fieldValue.length, originalEndIndex - currentOffset);

              if (newStartIndex < newEndIndex) { // Ensure valid segment
                newCitationSegments.push({
                  ...segment,
                  startIndex: newStartIndex,
                  endIndex: newEndIndex,
                });
              }
            }
          }
          support.citationSegments = newCitationSegments;
        } else {
          support.citationSegments = []; // Ensure it's an array if missing/null
        }
      }
    } else {
      fieldGroundingMetadata.groundingSupports = []; // Ensure it's an array if missing/null
    }
    
    allFieldsMetadata[fieldKey] = fieldGroundingMetadata;
    currentOffset += fieldValue.length;
  }
  return allFieldsMetadata;
}


export function CreateSiteProvider({ children }) {
  const [state, dispatch] = useReducer(createSiteReducer, initialState);
  const navigate = useNavigate();
  const toast = useToast();

  // Computed values
  const canProceed = useMemo(() => {
    const { currentStep, siteData, validationErrors, duplicateName } = state;
    
    if (duplicateName) return false;

    // Check if there are any validation errors for the current step
    const hasErrors = Object.keys(validationErrors).length > 0;
    if (hasErrors) return false;
    
    // Step-specific validation
    switch (currentStep) {
      case 0: // Basic Info
        return siteData.name?.trim().length > 0;
      
      case 1: // Location
        // Check if coordinates are present and valid - allow for zero values
        // as long as they're explicitly set (valid coordinates near equator/prime meridian)
        if (!siteData.location?.coordinates) return false;
        
        const lat = siteData.location.coordinates.lat;
        const lng = siteData.location.coordinates.lng;
        
        // More robust check - ensure values can be converted to valid numbers
        return lat !== undefined && lat !== null && !isNaN(Number(lat)) &&
               lng !== undefined && lng !== null && !isNaN(Number(lng));
      
      case 2: // Timeline
        // Allow proceeding with or without timeline data
        return true;
      
      case 3: // Media
        // Allow proceeding with or without media
        return true;
      
      case 4: // Review
        // All validations should be done at this point
        return true;
      
      default:
        return false;
    }
  }, [state.currentStep, state.siteData, state.validationErrors]);

  // Define steps
  const steps = [
    { title: 'Basic Info', description: 'Site identification' },
    { title: 'Location', description: 'Geographic details' },
    { title: 'Timeline', description: 'Historical context' },
    { title: 'Media', description: 'Photos and videos' },
    { title: 'Review', description: 'Final verification' }
  ];

  // Handle moving to next step
  const handleNext = useCallback(() => {
    if (state.currentStep < steps.length - 1) {
      dispatch({
        type: ActionTypes.SET_CURRENT_STEP,
        payload: state.currentStep + 1
      });
    }
  }, [state.currentStep, steps.length]);

  // Handle moving to previous step
  const handlePrev = useCallback(() => {
    if (state.currentStep > 0) {
      dispatch({
        type: ActionTypes.SET_CURRENT_STEP,
        payload: state.currentStep - 1
      });
    }
  }, [state.currentStep]);

  // Load drafts
  const loadDrafts = useCallback(async () => {
    try {
      const result = await getDrafts();
      if (result.success) {
        dispatch({
          type: ActionTypes.SET_DRAFTS,
          payload: result.data
        });
      }
    } catch (error) {
      console.error('Error loading drafts:', error);
    }
  }, []);

  // Handle saving as draft
  const handleSaveAsDraft = useCallback(async () => {
    try {
      dispatch({ type: ActionTypes.SET_SAVING_DRAFT, payload: true });
      
      const result = await saveDraft({
        ...state.siteData,
        updatedAt: new Date()
      }, state.currentDraftId);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // Update current draft ID
      dispatch({
        type: ActionTypes.SET_CURRENT_DRAFT_ID,
        payload: result.data.id
      });
      
      // Refresh drafts list
      await loadDrafts();
      
      toast({
        title: 'Draft saved',
        description: 'Your progress has been saved',
        status: 'success',
        duration: 3000
      });
      
      // Mark as not dirty after saving
      dispatch({ type: ActionTypes.SET_IS_DIRTY, payload: false });
      
      return result;
    } catch (error) {
      console.error('Error saving draft:', error);
      toast({
        title: 'Error saving draft',
        description: error.message,
        status: 'error',
        duration: 5000
      });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: ActionTypes.SET_SAVING_DRAFT, payload: false });
    }
  }, [state.siteData, state.currentDraftId, loadDrafts, toast]);

  // Validate all fields
  const validateAllFields = useCallback(() => {
    const errors = {};
    const { siteData } = state;
    
    // Basic info validation
    if (!siteData.name?.trim()) {
      errors.name = 'Site name is required';
    }
    
    // if (!siteData.description?.trim()) { // Made optional by user request
    //   errors.description = 'Description is required';
    // }
    
    // Location validation - more robust check for coordinates // Made optional by user request
    // if (!siteData.location?.coordinates) {
    //   errors['location.coordinates'] = 'Site location is required';
    // } else {
    //   const lat = siteData.location.coordinates.lat;
    //   const lng = siteData.location.coordinates.lng;
      
    //   // Check if values exist and can be converted to valid numbers
    //   const validLat = lat !== undefined && lat !== null && !isNaN(Number(lat));
    //   const validLng = lng !== undefined && lng !== null && !isNaN(Number(lng));
      
    //   if (!validLat || !validLng) {
    //     errors['location.coordinates'] = 'Site location is required';
    //   }
    // }
    
    // Construction Periods Era validation
    const validateEra = (value) => {
      if (!value) return true; // Allow empty values
      if (value.toLowerCase() === 'present') return true; // Allow 'present' value
      // Regex to match "YYYY BCE/CE" with an optional trailing "?"
      return /^\d+ (BCE|CE)\??$/.test(value);
    };

    if (siteData.constructionPeriods && Array.isArray(siteData.constructionPeriods)) {
      siteData.constructionPeriods.forEach((period, index) => {
        if (period.startEra && !validateEra(period.startEra)) {
          errors[`constructionPeriods[${index}].startEra`] = `Period ${index + 1} Start Era must be in format: number followed by BCE or CE, optionally ending with ? (e.g. 3000 BCE?)`;
        }
        if (period.endEra && !validateEra(period.endEra)) {
          errors[`constructionPeriods[${index}].endEra`] = `Period ${index + 1} End Era must be in format: number followed by BCE or CE, or 'present', optionally ending with ? (e.g. 2500 BCE?)`;
        }
      });
    }
    
    return errors;
  }, [state.siteData]);

  // Submit site
  const handleSubmit = useCallback(async () => {
    try {
      // Validate all required fields
      const errors = validateAllFields();
      
      if (Object.keys(errors).length > 0) {
        dispatch({
          type: ActionTypes.SET_VALIDATION_ERRORS,
          payload: errors
        });
        
        // Find the first step with errors
        let firstStepWithError = 0;
        Object.keys(errors).forEach(field => {
          if (field.startsWith('name')) { // Description is now optional
            firstStepWithError = Math.min(firstStepWithError, 0); // Basic Info Step
          } else if (field.startsWith('location')) { // Location is now optional for submission but errors might still point here if other location fields are invalid
            firstStepWithError = Math.min(firstStepWithError, 1); // Location Step
          } else if (field.startsWith('constructionPeriods')) {
            firstStepWithError = Math.min(firstStepWithError, 2); // Timeline Step
          }
          // Add checks for other steps if needed (e.g., Media)
        });
        
        // Go to the first step with errors
        dispatch({
          type: ActionTypes.SET_CURRENT_STEP,
          payload: firstStepWithError
        });
        
        toast({
          title: 'Validation error',
          description: 'Please fix the highlighted errors before submitting',
          status: 'error',
          duration: 5000
        });
        
        console.error('Validation failed. Specific errors:', errors);
        return { success: false, error: 'Validation failed' };
      }

      // Create site in Firebase
      const result = await createSite(state.siteData);
      
      if (!result.success) {
        throw new Error(result.error);
      }
      
      // If this was a draft, delete it
      if (state.currentDraftId) {
        await deleteDraft(state.currentDraftId);
      }
      
      toast({
        title: 'Success',
        description: 'Site created successfully',
        status: 'success',
        duration: 5000
      });
      
      // Reset state after successful submission
      dispatch({ type: ActionTypes.RESET_STATE });
      
      // Navigate to the new site
      navigate(`/site/${result.data.id}`);
      
      return result;
    } catch (error) {
      toast({
        title: 'Error creating site',
        description: error.message,
        status: 'error',
        duration: 5000
      });
      return { success: false, error: error.message };
    }
  }, [state.siteData, state.currentDraftId, validateAllFields, toast, navigate]);

  // Fetch AI site info
  const fetchAiSiteInfo = useCallback(async () => {
    try {
      if (!state.siteData.name) {
        throw new Error('Please enter a site name first');
      }
      
      dispatch({ type: ActionTypes.SET_AI_LOADING, payload: true });
      // Clear previous spell check data before fetching new info
      dispatch({ type: ActionTypes.SET_SPELL_CHECK_DATA, payload: null });
      
      const result = await aiService.getSiteInfo(state.siteData.name);
      
      if (!result.success || !result.data) {
        throw new Error('Invalid response from AI service');
      }
      
      // Update site data with AI information
      const formattedInfo = aiService.formatSiteInfo(result);
      //console.log('[CreateSiteContext] formattedInfo from aiService:', JSON.stringify(formattedInfo, null, 2));

      // Prepare construction periods update (existing logic)
      let updatedConstructionPeriods = state.siteData.constructionPeriods || [];
      // let updatedMainPeriodIndex = state.siteData.mainPeriodIndex; // This seems to be unused or handled within constructionPeriods

      if (formattedInfo.constructionPeriods && formattedInfo.constructionPeriods.length > 0) {
        updatedConstructionPeriods = formattedInfo.constructionPeriods.map(p => ({
          ...p,
          startEra: p.startEra || '',
          endEra: p.endEra || '',
          startEraApproximate: p.startEra?.endsWith('?') || false,
          endEraApproximate: p.endEra?.endsWith('?') || false,
          periodName: p.periodName || 'Unnamed Period',
          features: p.features || [],
          details: p.details || '',
          isMain: p.isMain !== undefined ? p.isMain : false
        }));
        // const aiMainIndex = updatedConstructionPeriods.findIndex(p => p.isMain);
        // updatedMainPeriodIndex = aiMainIndex !== -1 ? aiMainIndex : (updatedConstructionPeriods.length > 0 ? 0 : null);
      } else if (updatedConstructionPeriods.length === 0 && (formattedInfo.startEra || formattedInfo.endEra)) {
        const aiStartEra = formattedInfo.startEra || '';
        const aiEndEra = formattedInfo.endEra || '';
        updatedConstructionPeriods = [{
          periodName: 'Main Period (AI Generated)',
          startEra: aiStartEra,
          endEra: aiEndEra,
          startEraApproximate: aiStartEra.endsWith('?'),
          endEraApproximate: aiEndEra.endsWith('?'),
          features: formattedInfo.features || [],
          details: '',
          isMain: true
        }];
        // updatedMainPeriodIndex = 0;
      }

      const payloadUpdates = {
        name: state.siteData.name, // Preserve the name that initiated the fetch
        constructionPeriods: updatedConstructionPeriods,
      };

      // Handle non-section fields provided by AI
      if (formattedInfo.description !== undefined) payloadUpdates.description = formattedInfo.description;
      if (formattedInfo.historicalPeriod !== undefined) payloadUpdates.historicalPeriod = formattedInfo.historicalPeriod;
      if (formattedInfo.historicalSignificance !== undefined) payloadUpdates.historicalSignificance = formattedInfo.historicalSignificance;
      if (formattedInfo.keyDiscoveries !== undefined) payloadUpdates.keyDiscoveries = formattedInfo.keyDiscoveries;
      if (formattedInfo.currentStatus !== undefined) payloadUpdates.currentStatus = formattedInfo.currentStatus;
      if (formattedInfo.eraDisplay !== undefined) payloadUpdates.eraDisplay = formattedInfo.eraDisplay;
      if (formattedInfo.startEra !== undefined) payloadUpdates.startEra = formattedInfo.startEra;
      if (formattedInfo.endEra !== undefined) payloadUpdates.endEra = formattedInfo.endEra;
      if (formattedInfo.features !== undefined) payloadUpdates.features = formattedInfo.features;
      if (formattedInfo.sources !== undefined) payloadUpdates.sources = formattedInfo.sources;
      
      // Process citations to ensure structure
      if (formattedInfo.citations !== undefined) {
        if (Array.isArray(formattedInfo.citations)) {
          payloadUpdates.citations = formattedInfo.citations.map((cit, index) => {
            const { id, title, url, type, ...rest } = cit || {}; // Handle if cit is null/undefined, destructure id
            let parsedNumber;
            if (typeof id === 'string') {
              const match = id.match(/\d+/); // Extract number from id like "[1]" or "1"
              if (match) {
                parsedNumber = parseInt(match[0], 10);
              }
            }
            // Fallback to index + 1 if id is missing, not a string, or number cannot be parsed
            const finalNumber = (parsedNumber !== undefined) ? parsedNumber : (index + 1);

            return {
              ...rest, // Spread rest first
              id: id || `[${finalNumber}]`, // Ensure id is present, prefer original or reconstruct
              number: finalNumber,
              title: title || '',
              url: url || '',
              type: type || 'unknown',
            };
          });
        } else {
          // If formattedInfo.citations is defined but not an array (e.g. null), treat as empty
          payloadUpdates.citations = [];
        }
      }
      // If formattedInfo.citations is undefined, payloadUpdates.citations is not touched.

      if (formattedInfo.groundingArticles !== undefined) payloadUpdates.groundingArticles = formattedInfo.groundingArticles;
      if (formattedInfo.modelInfo !== undefined) payloadUpdates.modelInfo = formattedInfo.modelInfo;
      if (formattedInfo.usageMetadata !== undefined) payloadUpdates.usageMetadata = formattedInfo.usageMetadata;
      if (formattedInfo.spellCheck !== undefined) payloadUpdates.spellCheck = formattedInfo.spellCheck;
      if (formattedInfo.citationMetadata !== undefined) payloadUpdates.citationMetadata = formattedInfo.citationMetadata;


      // Handle section fields (AI-generated text fields)
      const aiSiteWideGroundingMetadata = formattedInfo.groundingMetadata;
      
      // Generate field-specific grounding metadata with re-scoped citation segments
      const fieldSpecificGroundingData = generateFieldSpecificGroundingMetadata(
        sectionsConfig,
        formattedInfo, // This is the aiData containing the text field values
        aiSiteWideGroundingMetadata
      );

      for (const section of sectionsConfig) {
        const key = section.field;
        if (formattedInfo.hasOwnProperty(key) && formattedInfo[key] !== undefined && formattedInfo[key] !== null) {
          payloadUpdates[key] = {
            value: String(formattedInfo[key]), // Ensure value is a string
            groundingMetadata: fieldSpecificGroundingData[key] || { groundingSupports: [] }, // Use re-scoped metadata
          };
        } else if (formattedInfo.hasOwnProperty(key) && (formattedInfo[key] === undefined || formattedInfo[key] === null)) {
          // If the field exists in AI response but is null/undefined, ensure it has a value and empty metadata
           payloadUpdates[key] = {
            value: '',
            groundingMetadata: fieldSpecificGroundingData[key] || { groundingSupports: [] },
          };
        }
      }
      
      // Ensure top-level groundingMetadata is also set (as a copy of site-wide, or empty object)
      // This is for any part of the app still relying on a single top-level groundingMetadata.
      // Field-specific metadata is now primary for SourceLinkedText.
      // For consistency, this top-level one should NOT be re-scoped, it represents the original AI output.
      payloadUpdates.groundingMetadata = JSON.parse(JSON.stringify(aiSiteWideGroundingMetadata || {}));

      dispatch({
        type: ActionTypes.SET_SITE_DATA,
        payload: payloadUpdates
      });
      
      // Grounding info and spell check data are now part of the main siteData payload.
      // Dispatching them separately might be redundant if components read from siteData.
      // However, if SET_GROUNDING_INFO or SET_SPELL_CHECK_DATA have specific consumers, keep them.
      // For now, assuming siteData is the source of truth.
      dispatch({ type: ActionTypes.SET_GROUNDING_INFO, payload: payloadUpdates.groundingMetadata });
      dispatch({ type: ActionTypes.SET_SPELL_CHECK_DATA, payload: payloadUpdates.spellCheck });
      
      // Set grounding info (still useful for direct access if needed, but now also in siteData)
      // This might be redundant if groundingMetadata is already in siteData.
      // dispatch({
      //   type: ActionTypes.SET_GROUNDING_INFO,
      //   payload: formattedInfo.groundingMetadata
      // });
      
      // Set spell check data (already in siteData via the main SET_SITE_DATA dispatch)
      // dispatch({
      //   type: ActionTypes.SET_SPELL_CHECK_DATA,
      //   payload: formattedInfo.spellCheck
      // });
      
      // Store search results in state if available (This seems unrelated to siteData update, keep separate)
      if (formattedInfo.searchResults && formattedInfo.searchResults.length > 0) {
        dispatch({
          type: ActionTypes.SET_SEARCH_RESULTS,
          payload: formattedInfo.searchResults
        });
        
        dispatch({
          type: ActionTypes.SET_SEARCH_QUERY,
          payload: formattedInfo.searchQuery || ''
        });
        
        console.log('Search results found:', formattedInfo.searchResults.length);
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching AI information:', error);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000
      });
      throw error;
    } finally {
      dispatch({ type: ActionTypes.SET_AI_LOADING, payload: false });
    }
  }, [state.siteData.name, toast]); // Removed state.siteData from dependencies as it's being set

  // --- Citation Management ---

  const getHighestCitationNumber = useCallback(() => {
    return state.siteData.citations?.reduce((max, cit) => Math.max(max, cit.number || 0), 0) || 0;
  }, [state.siteData.citations]);

  const addCitation = useCallback((newCitationData) => {
    const nextNumber = getHighestCitationNumber() + 1;
    const citationToAdd = {
      ...newCitationData,
      number: nextNumber,
      // Ensure default values for core fields if not provided
      type: newCitationData.type || 'other',
      title: newCitationData.title || '',
      author: newCitationData.author || '',
    };
    dispatch({
      type: ActionTypes.SET_SITE_DATA,
      payload: {
        citations: [...state.siteData.citations, citationToAdd]
      }
    });
    toast({
      title: 'Citation Added',
      status: 'success',
      duration: 2000,
    });
  }, [state.siteData.citations, getHighestCitationNumber, toast]);

  const updateCitation = useCallback((citationNumber, updatedCitationData) => {
    dispatch({
      type: ActionTypes.SET_SITE_DATA,
      payload: {
        citations: state.siteData.citations.map(cit =>
          cit.number === citationNumber ? { ...cit, ...updatedCitationData } : cit
        )
      }
    });
    toast({
      title: 'Citation Updated',
      status: 'success',
      duration: 2000,
    });
  }, [state.siteData.citations, toast]);

  const deleteCitation = useCallback((citationNumber) => {
    dispatch({
      type: ActionTypes.SET_SITE_DATA,
      payload: {
        citations: state.siteData.citations.filter(cit => cit.number !== citationNumber)
      }
    });
    toast({
      title: 'Citation Deleted',
      status: 'success',
      duration: 2000,
    });
  }, [state.siteData.citations, toast]);

  // --- End Citation Management ---


  const setSiteData = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_SITE_DATA, payload: data });
  }, []);

  const setShowExitModal = useCallback((show) => {
    dispatch({ type: ActionTypes.SET_SHOW_EXIT_MODAL, payload: show });
  }, []);

  const setLimitReachedModal = useCallback((show) => {
    dispatch({ type: ActionTypes.SET_LIMIT_REACHED_MODAL, payload: show });
  }, []);

  const setDuplicateNameStatus = useCallback((isDuplicate) => {
    dispatch({ type: ActionTypes.SET_DUPLICATE_NAME, payload: isDuplicate });
  }, []);

  const loadDraftById = useCallback((draftId) => {
    const draftToLoad = state.drafts.find(d => d.id === draftId);
    if (draftToLoad) {
      // Only show toast if loading a NEW draft or if no draft is currently loaded
      if (state.currentDraftId !== draftId) {
        toast({
          title: 'Draft loaded',
          description: `Loaded draft for "${draftToLoad.siteData.name || 'Untitled Site'}"`,
          status: 'info',
          duration: 3000
        });
      }
      dispatch({ type: ActionTypes.LOAD_DRAFT, payload: draftToLoad });
    }
  }, [state.drafts, toast]);

  // Load drafts on mount
  useEffect(() => {
    if (auth.currentUser) {
      loadDrafts();
    }
  }, [loadDrafts]);

  // Check for unsaved changes before unloading
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (state.isDirty && !state.showExitModal) {
        e.preventDefault();
        e.returnValue = '';
        
        // Show exit modal
        dispatch({ type: ActionTypes.SET_SHOW_EXIT_MODAL, payload: true });
        return '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state.isDirty, state.showExitModal]);

  // Context value
  const value = {
    ...state, // Includes siteData which has citations
    citations: state.siteData.citations || [], // Direct access for convenience
    steps,
    canProceed,
    handleNext,
    handlePrev,
    handleSubmit,
    handleSaveAsDraft,
    fetchAiSiteInfo,
    setSiteData,
    setShowExitModal,
    setLimitReachedModal,
    setDuplicateNameStatus,
    loadDrafts,
    loadDraftById,
    // Citation functions
    addCitation,
    updateCitation,
    deleteCitation,
    // Draft deletion
    deleteDraft: async (draftId) => {
      try {
        await deleteDraft(draftId);
        await loadDrafts();
        if (state.currentDraftId === draftId) {
          dispatch({ type: ActionTypes.SET_CURRENT_DRAFT_ID, payload: null });
        }
        toast({
          title: 'Draft deleted',
          status: 'success',
          duration: 3000
        });
      } catch (error) {
        console.error('Error deleting draft:', error);
        toast({
          title: 'Error deleting draft',
          description: error.message,
          status: 'error',
          duration: 5000
        });
      }
    }
  };

  return (
    <CreateSiteContext.Provider value={value}>
      {children}
    </CreateSiteContext.Provider>
  );
}

export function useCreateSite() {
  const context = useContext(CreateSiteContext);
  if (!context) {
    throw new Error('useCreateSite must be used within a CreateSiteProvider');
  }
  return context;
}
